/**
 * Mobile side-nav swipe gestures: edge swipe right to open, swipe left to close.
 * Thresholds match core/utils/swipe-handlers.js for consistency.
 */

const CONFIG = {
  minDistanceX: 40,
  minDistanceRatio: 1.15,
  maxDurationMs: 900,
  edgeZonePx: 24,
};

/**
 * @param {HTMLElement | null} node
 */
const isInsideHorizontalScroller = (node) => {
  let el = node;
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el);
    const overflowX = style.overflowX;
    if ((overflowX === 'auto' || overflowX === 'scroll') && el.scrollWidth > el.clientWidth) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
};

/**
 * @param {object} params
 * @param {() => boolean} params.getIsDrawerMode
 * @param {() => boolean} params.getIsOpen
 * @param {(isOpen: boolean) => void} params.setOpen
 * @param {HTMLElement} params.sideNav
 * @param {HTMLElement} params.overlay
 * @returns {() => void}
 */
export function setupSideNavSwipe({ getIsDrawerMode, getIsOpen, setOpen, sideNav, overlay }) {
  if (!sideNav || !overlay) return () => {};

  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartTime = 0;
  let isTracking = false;
  let didLockHorizontal = false;
  /** @type {'open' | 'close' | null} */
  let gestureMode = null;

  const reset = () => {
    isTracking = false;
    didLockHorizontal = false;
    gestureMode = null;
    touchStartX = 0;
    touchStartY = 0;
    touchStartTime = 0;
  };

  const handleTouchStart = (e) => {
    if (!getIsDrawerMode() || document.body.classList.contains('modal-open')) {
      reset();
      return;
    }
    if (!e.touches || e.touches.length !== 1) return;

    const t = e.touches[0];
    const target = e.target instanceof HTMLElement ? e.target : null;
    if (target && isInsideHorizontalScroller(target)) return;

    const isOpen = getIsOpen();

    if (!isOpen && t.clientX <= CONFIG.edgeZonePx) {
      gestureMode = 'open';
    } else if (isOpen && target && (sideNav.contains(target) || overlay.contains(target))) {
      gestureMode = 'close';
    } else {
      return;
    }

    touchStartX = t.clientX;
    touchStartY = t.clientY;
    touchStartTime = Date.now();
    isTracking = true;
    didLockHorizontal = false;
  };

  const handleTouchMove = (e) => {
    if (!isTracking || !e.touches || e.touches.length !== 1) return;

    const t = e.touches[0];
    const dx = t.clientX - touchStartX;
    const dy = t.clientY - touchStartY;

    if (didLockHorizontal) {
      e.preventDefault();
      return;
    }

    if (Math.abs(dx) >= 12 && Math.abs(dx) >= Math.abs(dy) * CONFIG.minDistanceRatio) {
      didLockHorizontal = true;
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e) => {
    if (!isTracking || !gestureMode || !e.changedTouches || e.changedTouches.length !== 1) {
      reset();
      return;
    }

    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX;
    const dy = t.clientY - touchStartY;
    const dt = Date.now() - touchStartTime;
    const mode = gestureMode;

    reset();

    if (!getIsDrawerMode()) return;
    if (dt > CONFIG.maxDurationMs) return;
    if (Math.abs(dx) < CONFIG.minDistanceX) return;
    if (Math.abs(dx) < Math.abs(dy) * CONFIG.minDistanceRatio) return;

    if (mode === 'open' && dx > 0 && !getIsOpen()) {
      setOpen(true);
    } else if (mode === 'close' && dx < 0 && getIsOpen()) {
      setOpen(false);
    }
  };

  const handleTouchCancel = () => {
    reset();
  };

  document.addEventListener('touchstart', handleTouchStart, { passive: true });
  document.addEventListener('touchmove', handleTouchMove, { passive: false });
  document.addEventListener('touchend', handleTouchEnd, { passive: true });
  document.addEventListener('touchcancel', handleTouchCancel, { passive: true });

  return () => {
    document.removeEventListener('touchstart', handleTouchStart);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
    document.removeEventListener('touchcancel', handleTouchCancel);
  };
}
